import { useEffect, useRef, type ReactNode } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

type Props = {
  children: ReactNode;
  /** Entrance delay in seconds — used for editorial stagger inside a composition. */
  delay?: number;
  /** Desktop internal parallax travel in px (each direction). 0 = no parallax. */
  parallax?: number;
  /** Desktop starting scale of the settle. */
  fromScale?: number;
  /** Desktop starting vertical offset in px. */
  fromY?: number;
  duration?: number;
};

/**
 * PHOTOGRAPHIC SETTLE + INTERNAL PARALLAX.
 * Sits INSIDE an existing overflow-hidden photo frame. The frame never moves.
 * Layers: [parallax layer: y + overscan scale] > [settle layer: scale / y / opacity] > image.
 * The image itself keeps its own hover transform, so no element has two writers on `transform`.
 * Without JS (or with reduced motion) both layers are plain full-size wrappers — the photo is always visible.
 */
export default function PhotoMotion({ children, delay = 0, parallax = 0, fromScale = 1.055, fromY = 24, duration = 1.15 }: Props) {
  const plxRef = useRef<HTMLDivElement>(null);
  const settleRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const plx = plxRef.current;
    const settle = settleRef.current;
    if (!plx || !settle) return undefined;

    const mm = gsap.matchMedia();
    mm.add(
      {
        desktop: '(min-width: 1024px) and (prefers-reduced-motion: no-preference)',
        tablet: '(min-width: 768px) and (max-width: 1023px) and (prefers-reduced-motion: no-preference)',
        mobile: '(max-width: 767px) and (prefers-reduced-motion: no-preference)',
      },
      (ctx) => {
        const { desktop, tablet, mobile } = ctx.conditions as Record<string, boolean>;
        if (!desktop && !tablet && !mobile) return;

        const amp = desktop ? 1 : tablet ? 0.6 : 0.4;
        const scale0 = 1 + (fromScale - 1) * amp;
        const y0 = fromY * amp;
        const travel = mobile ? 0 : parallax * (desktop ? 1 : 0.6);

        // Entrance: short eased tween, plays once, never re-hides on reverse scroll.
        gsap.fromTo(
          settle,
          { scale: scale0, y: y0, opacity: 0.92, transformOrigin: '50% 50%' },
          {
            scale: 1,
            y: 0,
            opacity: 1,
            duration: desktop ? duration : duration * 0.85,
            delay,
            ease: 'power3.out',
            clearProps: 'transform,opacity,transformOrigin',
            scrollTrigger: { trigger: plx, start: 'top 90%', once: true },
          },
        );

        // Internal parallax: frame fixed, content drifts. Overscan scale covers the travel so no edge shows.
        if (travel > 0) {
          gsap.fromTo(
            plx,
            { y: -travel, scale: () => 1 + (travel * 2 + 6) / Math.max(1, plx.offsetHeight) },
            {
              y: travel,
              ease: 'none',
              scrollTrigger: { trigger: plx, start: 'top bottom', end: 'bottom top', scrub: true, invalidateOnRefresh: true },
            },
          );
        }
      },
    );

    // Late-loading images change nothing in layout (frames have fixed ratios), but refresh positions anyway.
    const img = plx.querySelector('img');
    const onLoad = () => ScrollTrigger.refresh();
    if (img && !img.complete) img.addEventListener('load', onLoad, { once: true });

    return () => {
      if (img) img.removeEventListener('load', onLoad);
      mm.revert(); // kills only this component's tweens + triggers and restores inline styles
    };
  }, [delay, parallax, fromScale, fromY, duration]);

  return (
    <div ref={plxRef} className="w-full h-full">
      <div ref={settleRef} className="w-full h-full">
        {children}
      </div>
    </div>
  );
}